import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  OverlayView,
  Polygon
} from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { Badge, Button } from 'react-bootstrap';
import { ArrowsMove, Box, Building, Person } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import './GoogleMap.css';
import { getModelLabel } from '../../../helpers/formHelpers';
import { isAdmin } from '../../../helpers/roleHelpers';
import MarkerIcon from './Marker.svg';

const containerStyle = {
  width: '100%',
  height: '100%'
};

const defaultPolygonOptions = {
  strokeColor: '#FFFF00',
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: '#FFFF00',
  fillOpacity: 0.35,
  editable: false,
  draggable: false
};

const selectedPolygonOptions = {
  strokeColor: '#00FF00',
  strokeOpacity: 1,
  strokeWeight: 2,
  fillColor: '#00FF00',
  fillOpacity: 0.8,
  editable: true,
  draggable: true
};

const dimmedPolygonOptions = {
  strokeColor: '#A9A9A9',
  fillColor: '#A9A9A9',
  fillOpacity: 0.5
};

/**
 * 
 * Empty Slot: Grayish
Stock Unit: Green
Contracted: Red
 */

const emptySlotColorOptions = { strokeColor: '#f4ffe6', fillColor: '#f4ffe6' };
const occupiedContractedSlots = { strokeColor: '#dc3545', fillColor: '#dc3545' };
const occupiedInStockSlots = { strokeColor: '#28a745', fillColor: '#28a745' };
const closedSlotColorOptions = { strokeColor: 'black', fillColor: '#FF6347' };
const officeBuildingSlot = { strokeColor: 'yellow', fillColor: 'yellow' };
const drawStyles = {
  fillColor: '#FFFF00',
  outlineColor: '#FFFF00',
  closingPointWidth: 0,
  closingPointOutlineWidth: 0,
  coordinatePointWidth: 0,
  coordinatePointOutlineWidth: 0,
  snappingPointWidth: 0,
  snappingPointOutlineWidth: 0
};

const countPolygonVertices = ring => {
  if (!ring?.length) return 0;

  let count = ring.length;
  const first = ring[0];
  const last = ring[count - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    count -= 1;
  }
  return count;
};

const geoJsonPolygonToPaths = feature => {
  const ring = feature?.geometry?.coordinates?.[0];
  if (!ring?.length) return null;

  let paths = ring.map(([lng, lat]) => ({ lat, lng }));
  const first = paths[0];
  const last = paths[paths.length - 1];
  if (first.lat === last.lat && first.lng === last.lng) {
    paths = paths.slice(0, -1);
  }
  return paths;
};

const DRAW_INIT_TIMEOUT_MS = 10000;

const getCurtailmentCutOffDate = () => {
  const today = new Date();

  // Set year to previous year
  today.setFullYear(today.getFullYear() - 1);

  // Set to the next month
  today.setMonth(today.getMonth() + 1);

  // Set to the last day of the intended month
  today.setDate(0);

  // Set the time to just before midnight
  today.setHours(23, 59, 59, 999);

  return today.getTime();
};

function Map({
  center,
  zoom = 12,
  rotation = 0,
  polygons = [],
  onAddNewPolygon,
  onPolygonUpdate,
  disableDrawing,
  infoWindowMeta = {},
  setInfoWindowMeta,
  handlePolygonClick,
  InfoWindowContent,
  onMapPropertiesChange,
  infoWindowToolbarExtra = null,
  hideInfoWindowToolbar = false
}) {
  const [internalPolygons, setInternalPolygons] = useState([]);
  const [map, setMap] = useState(null);
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [selectedPolygonIndex, setSelectedPolygonIndex] = useState(null);
  const [activeDrawMode, setActiveDrawMode] = useState(null);
  const [drawReady, setDrawReady] = useState(false);
  const [drawInitFailed, setDrawInitFailed] = useState(false);
  const polygonRefs = useRef([]);
  const [currentRotation, setCurrentRotation] = useState(0);
  const [currentScale, setCurrentScale] = useState(1);
  const [liveRotation, setLiveRotation] = useState(0);
  const [liveScale, setLiveScale] = useState(1);
  const originalPolygon = useRef(null); // Store the original unmodified polygon
  const terraDrawRef = useRef(null);
  const onAddNewPolygonRef = useRef(onAddNewPolygon);
  const curtailmentCutOffDate = useMemo(() => getCurtailmentCutOffDate(), []);

  useEffect(() => {
    onAddNewPolygonRef.current = onAddNewPolygon;
  }, [onAddNewPolygon]);

  useEffect(() => {
    if (!map || disableDrawing || !isAdmin()) {
      if (terraDrawRef.current) {
        terraDrawRef.current.stop();
        terraDrawRef.current = null;
      }
      setActiveDrawMode(null);
      setDrawReady(false);
      setDrawInitFailed(false);
      return;
    }

    let draw;
    let initTimeoutId;
    let cancelled = false;

    const failInit = () => {
      if (cancelled) return;

      setDrawInitFailed(true);
      setDrawReady(false);
      setActiveDrawMode(null);
      if (draw) {
        draw.stop();
        draw = undefined;
      }
      terraDrawRef.current = null;
      toast.error('Map drawing tools failed to load. Please refresh the page.');
    };

    const initDraw = () => {
      if (cancelled || terraDrawRef.current) {
        return;
      }

      const mapDiv = map.getDiv();
      if (mapDiv && !mapDiv.id) {
        mapDiv.id = 'google-map-draw';
      }

      try {
        draw = new TerraDraw({
          adapter: new TerraDrawGoogleMapsAdapter({ map, lib: window.google.maps }),
          modes: [
            new TerraDrawPolygonMode({
              styles: drawStyles,
              validation: (feature, { updateType }) => {
                if (updateType === 'finish') {
                  const vertexCount = countPolygonVertices(feature.geometry?.coordinates?.[0]);
                  return vertexCount >= 4 ? { valid: true } : { valid: false };
                }
                return { valid: true };
              }
            }),
            new TerraDrawRectangleMode({ styles: drawStyles })
          ]
        });

        draw.on('ready', () => {
          if (cancelled) return;

          window.clearTimeout(initTimeoutId);
          draw.setMode('static');
          terraDrawRef.current = draw;
          setDrawReady(true);
          setDrawInitFailed(false);
        });

        draw.on('finish', (id, { action, mode }) => {
          if (action !== 'draw') return;

          const activeDraw = terraDrawRef.current;
          if (!activeDraw) return;

          const paths = geoJsonPolygonToPaths(activeDraw.getSnapshotFeature(id));
          if (!paths || (mode === 'polygon' && paths.length < 4)) {
            if (mode === 'polygon' && paths) {
              toast.error('A slot must have atleast four vertex');
            }
            activeDraw.removeFeatures([id]);
            return;
          }

          activeDraw.removeFeatures([id]);
          activeDraw.setMode('static');
          setActiveDrawMode(null);
          onAddNewPolygonRef.current(paths);
        });

        draw.start();

        initTimeoutId = window.setTimeout(() => {
          if (!cancelled && !terraDrawRef.current) {
            failInit();
          }
        }, DRAW_INIT_TIMEOUT_MS);
      } catch {
        failInit();
      }
    };

    const idleListener = window.google.maps.event.addListenerOnce(map, 'idle', initDraw);

    return () => {
      cancelled = true;
      window.clearTimeout(initTimeoutId);
      window.google.maps.event.removeListener(idleListener);
      if (draw) {
        draw.stop();
      }
      terraDrawRef.current = null;
      setActiveDrawMode(null);
      setDrawReady(false);
      setDrawInitFailed(false);
    };
  }, [map, disableDrawing]);

  // To store the modified polygon during transformations
  const modifiedPolygon = useRef(null);

  useEffect(() => {
    setInternalPolygons(polygons || []);
  }, [polygons]);

  const onLoad = useCallback(
    mapInstance => {
      setMap(mapInstance);

      // Trigger the onMapPropertiesChange callback when map center, zoom, or bounds change
      mapInstance.addListener('center_changed', () => {
        const center = mapInstance.getCenter();
        const zoom = mapInstance.getZoom();
        const bounds = mapInstance.getBounds();
        onMapPropertiesChange({ center: center.toJSON(), zoom, bounds: bounds?.toJSON() });
      });

      mapInstance.addListener('zoom_changed', () => {
        const center = mapInstance.getCenter();
        const zoom = mapInstance.getZoom();
        const bounds = mapInstance.getBounds();
        onMapPropertiesChange({ center: center.toJSON(), zoom, bounds: bounds?.toJSON() });
      });

      mapInstance.addListener('bounds_changed', () => {
        const center = mapInstance.getCenter();
        const zoom = mapInstance.getZoom();
        const bounds = mapInstance.getBounds();
        onMapPropertiesChange({ center: center.toJSON(), zoom, bounds: bounds?.toJSON() });
      });
    },
    [onMapPropertiesChange]
  );

  const selectDrawTool = tool => {
    const draw = terraDrawRef.current;
    if (!draw) return;

    if (tool === 'hand') {
      draw.setMode('static');
      setActiveDrawMode(null);
      return;
    }

    const isActive = activeDrawMode === tool;
    const nextMode = isActive ? 'static' : tool;
    draw.setMode(nextMode);
    setActiveDrawMode(isActive ? null : tool);
  };

  const onUnmount = useCallback(() => setMap(null), []);

  const handlePolygonClickInternal = (index, event) => {
    if (!isRepositioning) {
      handlePolygonClick(index, event);
      setSelectedPolygonIndex(index);
      originalPolygon.current = [...internalPolygons[index].paths]; // Store original state
      modifiedPolygon.current = [...internalPolygons[index].paths]; // Store initial to-be-modified state
      setCurrentRotation(0);
      setCurrentScale(1);
    }
  };

  const handleRepositionClick = () => {
    setIsRepositioning(true);
    setInfoWindowMeta(null); // Hide popup
  };

  const handleDoneClick = () => {
    if (modifiedPolygon.current && selectedPolygonIndex !== null) {
      const newPolygon = {
        ...internalPolygons[selectedPolygonIndex],
        paths: rotateAndScalePolygon(originalPolygon.current, liveRotation, liveScale) // Always start from original state
      };
      onPolygonUpdate(newPolygon); // Only make one API call here
      setIsRepositioning(false);
      setLiveRotation(0);
      setLiveScale(1);
      setCurrentRotation(0);
      setCurrentScale(1);
      setInfoWindowMeta({ polygonIndex: selectedPolygonIndex });
    }
  };

  const handleCloseInfoWindow = () => {
    setSelectedPolygonIndex(null);
    setInfoWindowMeta(null);
    setIsRepositioning(false);
  };

  const calculateCenter = paths => {
    if (!window.google) return null;

    const bounds = new window.google.maps.LatLngBounds();
    paths.forEach(path => {
      bounds.extend(new window.google.maps.LatLng(path.lat, path.lng));
    });
    return bounds.getCenter().toJSON();
  };

  const calculateLeftTopCorner = paths => {
    if (!paths || paths.length === 0) return null;

    // Start with the first point as a candidate
    let leftTopPoint = paths[0];

    paths.forEach(path => {
      // Compare to find the leftmost and topmost point
      if (path.lng < leftTopPoint.lng || (path.lng === leftTopPoint.lng && path.lat > leftTopPoint.lat)) {
        leftTopPoint = path;
      }
    });

    return leftTopPoint;
  };

  const rotatePolygon = (angle, paths) => {
    if (!paths || paths.length === 0) return paths;

    const center = calculateCenter(paths);

    if (!center) return paths;

    const rad = (angle * Math.PI) / 180;

    return paths.map(({ lat, lng }) => {
      if (lat == null || lng == null) return { lat, lng };

      const x = lng - center.lng;
      const y = lat - center.lat;
      return {
        lat: center.lat + (x * Math.sin(rad) + y * Math.cos(rad)),
        lng: center.lng + (x * Math.cos(rad) - y * Math.sin(rad))
      };
    });
  };

  const scalePolygon = (factor, paths) => {
    if (!paths || paths.length === 0) return paths;

    const center = calculateCenter(paths);

    if (!center) return paths;

    return paths.map(({ lat, lng }) => {
      if (lat == null || lng == null) return { lat, lng };

      const x = lng - center.lng;
      const y = lat - center.lat;
      return {
        lat: center.lat + y * factor,
        lng: center.lng + x * factor
      };
    });
  };

  // Recalculate all transformations from scratch
  const applyTransformationsFromOriginal = (paths, rotation, scale) => {
    const rotatedPaths = rotatePolygon(rotation, paths);
    const scaledPaths = scalePolygon(scale, rotatedPaths);
    return scaledPaths;
  };

  const rotateAndScalePolygon = (paths, angle, factor) => {
    const scaledPaths = scalePolygon(factor, paths); // First apply scaling
    const rotatedPaths = rotatePolygon(angle, scaledPaths); // Then apply rotation
    return rotatedPaths;
  };

  const handleRotationChange = angle => {
    setCurrentRotation(angle);
    setLiveRotation(angle);
  };

  const handleScaleChange = factor => {
    setCurrentScale(factor);
    setLiveScale(factor);
  };

  // Smooth real-time update for sliders without cumulative transformations
  useEffect(() => {
    const interval = setInterval(() => {
      if (isRepositioning && selectedPolygonIndex !== null) {
        // Always start from the original polygon, then apply transformations
        const transformedPolygon = applyTransformationsFromOriginal(originalPolygon.current, liveRotation, liveScale);
        modifiedPolygon.current = transformedPolygon;
      }
    }, 10); // Update every 10ms

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [liveRotation, liveScale, isRepositioning, selectedPolygonIndex]);

  const resetSliders = () => {
    setCurrentRotation(0);
    setCurrentScale(1);
  };

  const updatePolygonFromDragging = (polygon, index) => {
    const paths = polygon
      .getPath()
      .getArray()
      .map(latlng => ({
        lat: latlng.lat(),
        lng: latlng.lng()
      }));

    // Update the local modifiedPolygon reference (no API call)
    modifiedPolygon.current = paths;
    originalPolygon.current = paths; // Persist dragging changes to originalPolygon
  };

  return (
    <>
      <LoadScript
       googleMapsApiKey={process.env.REACT_APP_MAP_KEY}>
        <div className="google-map-wrapper">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={zoom}
          heading={rotation}
          onLoad={onLoad}
          onUnmount={onUnmount}
          tilt={0}
          mapTypeId="satellite"
          onClick={handleCloseInfoWindow}
        >
          {internalPolygons?.length > 0 &&
            internalPolygons.map((polygon, index) => (
              <React.Fragment key={polygon._id}>
                <Polygon
                  paths={
                    index === selectedPolygonIndex && isRepositioning
                      ? rotateAndScalePolygon(modifiedPolygon.current, liveRotation, liveScale)
                      : polygon.paths
                  }
                  onLoad={ref => (polygonRefs.current[index] = ref)}
                  onClick={event => handlePolygonClickInternal(index, event)}
                  options={
                    index === selectedPolygonIndex && isRepositioning
                      ? selectedPolygonOptions
                      : index === selectedPolygonIndex
                      ? { ...defaultPolygonOptions }
                      : isRepositioning
                      ? dimmedPolygonOptions
                      : {
                          ...defaultPolygonOptions,
                          ...(polygon.status === 'Closed'
                            ? closedSlotColorOptions
                            : polygon.inventory && polygon.inventory.contract
                            ? occupiedContractedSlots
                            : polygon.inventory
                            ? occupiedInStockSlots
                            : polygon.isOfficeBuilding
                            ? officeBuildingSlot
                            : emptySlotColorOptions)
                        }
                  }
                  editable={index === selectedPolygonIndex && isRepositioning}
                  draggable={index === selectedPolygonIndex && isRepositioning}
                  onMouseUp={event => {
                    if (index === selectedPolygonIndex && isRepositioning) {
                      updatePolygonFromDragging(polygonRefs.current[index], index); // Update modified polygon on drag
                    }
                  }}
                />
                {!isRepositioning && (
                  <>
                    {polygon?.isOfficeBuilding && (
                      <OverlayView
                        position={calculateCenter(polygon?.paths)}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <img
                          src={MarkerIcon}
                          alt="Office Building"
                          title="Office Building"
                          width={32}
                          height={40}
                          style={{
                            display: 'block',
                            transform: 'translate(-50%, -100%)',
                            marginLeft: '50%',
                            pointerEvents: 'none'
                          }}
                        />
                      </OverlayView>
                    )}
                    <OverlayView
                      position={calculateCenter(polygon?.paths)}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div className="w-100">
                        {polygon?.inventory && (
                          <div className="text-white mb-2">
                            {[
                              {
                                text: `${polygon?.inventory?.serialA}`,
                                Icon: Building
                              },
                              {
                                text: `${polygon?.inventory?.manufacturer?.factoryShortName || ''}${
                                  polygon?.inventory?.manufacturer?.factoryShortName ? ' - ' : ''
                                }${getModelLabel(polygon?.inventory?.model)}`,
                                Icon: Box
                              },
                              {
                                text: `${polygon?.inventory?.bedRooms ?? '-'}/${polygon?.inventory?.bathRooms ?? '-'}`,
                                Icon: () => 'B/B:'
                              },
                              { text: polygon?.inventory?.contract?.buyer, Icon: Person }
                            ]
                              .filter(item => item.text)
                              .map(({ text, Icon }) => (
                                <span
                                  style={{ width: 100, overflowWrap: 'break-word' }}
                                  className="d-flex font-weight-bold text-white align-items-center tinyFont mt-1"
                                >
                                  <Icon size={12} />
                                  <span className="mx-1"> {text || 'N/A'}</span>
                                </span>
                              ))}
                            {polygon?.inventory?.invoiceDate &&
                              new Date(polygon?.inventory?.invoiceDate).getTime() < curtailmentCutOffDate && (
                                <Badge style={{ fontSize: 8 }} variant="warning" className="px-1 tinyFont">
                                  <span>Curtailment</span>
                                </Badge>
                              )}
                          </div>
                        )}
                      </div>
                    </OverlayView>
                    <OverlayView
                      position={calculateLeftTopCorner(polygon?.paths)}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div className="p-1">
                        {polygon?.number && (
                          <Badge className="px-1" variant="dark">
                            #{polygon?.number}
                          </Badge>
                        )}
                      </div>
                    </OverlayView>
                  </>
                )}
              </React.Fragment>
            ))}

          {infoWindowMeta?.polygonIndex !== undefined && infoWindowMeta?.polygonIndex !== null && (
            <InfoWindow
              position={calculateCenter(internalPolygons[infoWindowMeta.polygonIndex]?.paths)}
              onCloseClick={handleCloseInfoWindow}
              options={{ minWidth: 360 }}
            >
              <div style={{ minWidth: 360 }}>
                {!hideInfoWindowToolbar && (isAdmin() || infoWindowToolbarExtra) && (
                  <div className="info-window-toolbar">
                    {isAdmin() && (
                      <Button
                        variant="primary"
                        size="sm"
                        className="info-window-toolbar-btn"
                        onClick={handleRepositionClick}
                      >
                        <ArrowsMove style={{ fontSize: '1rem' }} />
                        Transform
                      </Button>
                    )}
                    {infoWindowToolbarExtra}
                  </div>
                )}
                {InfoWindowContent()}
              </div>
            </InfoWindow>
          )}

          {isRepositioning && selectedPolygonIndex !== null && (
            <div className="floating-transformation-menu">
              <h5>Transform Polygon</h5>
              <label>Rotate: {currentRotation}°</label>
              <input
                type="range"
                value={currentRotation}
                min={-180}
                max={180}
                onChange={e => handleRotationChange(parseFloat(e.target.value))}
              />
              <br />
              <label>Scale: {currentScale}x</label>
              <input
                type="range"
                value={currentScale}
                min={0.25}
                max={4}
                step={0.01}
                onChange={e => handleScaleChange(parseFloat(e.target.value))}
              />
              <Button variant="primary" onClick={handleDoneClick}>
                Finish
              </Button>
            </div>
          )}
        </GoogleMap>
        {!disableDrawing && isAdmin() && (
          <div
            className={`map-drawing-controls${drawReady ? '' : ' map-drawing-controls--loading'}${
              drawInitFailed ? ' map-drawing-controls--failed' : ''
            }`}
            aria-busy={!drawReady && !drawInitFailed}
            title={
              drawInitFailed
                ? 'Drawing tools failed to load'
                : drawReady
                ? undefined
                : 'Loading drawing tools...'
            }
          >
            <button
              type="button"
              className={`map-drawing-control-btn${activeDrawMode === null && drawReady ? ' active' : ''}`}
              title="Pan map"
              aria-label="Pan map"
              disabled={!drawReady}
              onClick={() => selectDrawTool('hand')}
            >
              <ArrowsMove size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`map-drawing-control-btn${activeDrawMode === 'polygon' ? ' active' : ''}`}
              title="Draw polygon"
              aria-label="Draw polygon"
              aria-pressed={activeDrawMode === 'polygon'}
              disabled={!drawReady}
              onClick={() => selectDrawTool('polygon')}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 8l4-4 6 2 5 6-3 5-7 1-5-10z" fill="currentColor" fillOpacity="0.35" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>
            <button
              type="button"
              className={`map-drawing-control-btn${activeDrawMode === 'rectangle' ? ' active' : ''}`}
              title="Draw rectangle"
              aria-label="Draw rectangle"
              aria-pressed={activeDrawMode === 'rectangle'}
              disabled={!drawReady}
              onClick={() => selectDrawTool('rectangle')}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="5" y="7" width="14" height="10" fill="currentColor" fillOpacity="0.35" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>
          </div>
        )}
        </div>
      </LoadScript>
    </>
  );
}

export default Map;
